import React,{useEffect,useRef,useState} from 'react';
import {loadHost,setHostAccount,AccountRevocation} from '../../services/liveReviewApi';
import {accountNote} from './account-note';
import {ACCOUNT_REASONS,AccountReasonCode} from './account-reasons';
import './decision-queue.css';

/** What a suspension cut off, in words; a live that is still running is said first, never hidden. */
export function revocationText(r:AccountRevocation|null|undefined):{text:string;warn:boolean}|null{
  if(!r)return null;
  const parts:string[]=[],live=r.live;
  if(live.stillLive.length||live.error)return {warn:true,text:live.error
    ?`Their live could not be checked (${live.error}). Stop it from the room.`
    :`${live.stillLive.length===1?'A live is':`${live.stillLive.length} lives are`} still running. Stop it from the room.`};
  if(live.ended.length)parts.push(live.ended.length===1?'Their live was ended':`${live.ended.length} lives were ended`);
  if(live.removed.length)parts.push(`removed from ${live.removed.length} co-host seat${live.removed.length===1?'':'s'}`);
  if(!r.sockets.revoked)return {warn:true,text:[...parts,'their open connections could not be closed — they will be signed out when their session next refreshes'].join('; ')+'.'};
  parts.push(parts.length?'their open connections were closed':'Their open connections were closed');
  return {warn:false,text:parts.join(', ')+'.'};
}

/** Device ban (Bek D2: every suspension bans the phones). Said before the moderator confirms; nothing when there is nothing to say. */
export function devicesLine(d:{phones:number;linkedAccounts:number}|null|undefined):string|null{
  if(!d||!d.phones)return null;
  const phones=d.phones===1?'Their 1 phone will':`Their ${d.phones} phones will`;
  return `${phones} be banned too`+(d.linkedAccounts?` (${d.linkedAccounts===1?'1 other account':`${d.linkedAccounts} other accounts`} on ${d.phones===1?'it':'them'}).`:'.');
}

/** Bek 2026-10-03: "need suspension on the livestream page and everywhere". One control for suspending or restoring a
 *  user's whole account, the same on every screen that shows a host: a reason, what it does, then the server's answer.
 *
 *  `status` undefined means the screen does not know it, so the control asks the host page once; null means the profile
 *  carries no status, which is active. */
export default function AccountAction({userId,name,status,caseId,defaultReason='',showStatus=true,onChanged}:{
  userId:string;name:string;status?:string|null;caseId?:string;defaultReason?:string;showStatus?:boolean;
  onChanged?:(status:string)=>void}){
  const [known,setKnown]=useState<string|null|undefined>(status);
  const [open,setOpen]=useState(false),[reason,setReason]=useState(''),[reasonCode,setReasonCode]=useState<AccountReasonCode|''>(''),[busy,setBusy]=useState(false),[error,setError]=useState('');
  const [outcome,setOutcome]=useState<{text:string;warn:boolean}|null>(null);
  const box=useRef<HTMLTextAreaElement>(null);
  const [counts,setCounts]=useState<{phones:number;linkedAccounts:number}|null>(null);
  useEffect(()=>{setKnown(status);setOpen(false);setError('');},[status,userId]);
  useEffect(()=>{setOutcome(null);},[userId]);
  useEffect(()=>{
    if(status!==undefined||!userId)return;
    let closed=false;
    loadHost(userId).then(h=>{if(!closed)setKnown(h.account?.status??h.profile.AccountStatus??null);}).catch(()=>{if(!closed)setKnown(null);});
    return()=>{closed=true;};
  },[status,userId]);
  useEffect(()=>{if(open)box.current?.focus();},[open]);
  // Only a suspension bans phones; the counts come with the host summary, and an unknown count says nothing.
  useEffect(()=>{
    setCounts(null);
    if(!open||!userId||known==='suspended')return;
    let closed=false;
    Promise.resolve(loadHost(userId)).then(h=>{if(!closed)setCounts(h?.devices??null);}).catch(()=>{});
    return()=>{closed=true;};
  },[open,known,userId]);
  const current=known===undefined?undefined:known??'active';
  if(!userId||current===undefined)return null;
  // Deleted and blocked accounts are not ours to change from here.
  if(current!=='active'&&current!=='suspended')return showStatus?<span className="lc-chip">Account {current}</span>:null;
  const action=current==='suspended'?'restore':'suspend';
  const apply=async()=>{
    if(busy)return;
    const why=reason.trim();
    if(why.length<10){setError('Give a specific reason of at least ten characters. It is kept with the account.');return;}
    if(action==='suspend'&&!reasonCode){setError('Choose the reason the person will be shown.');return;}
    setBusy(true);setError('');
    try{
      const summary=await setHostAccount(userId,{action,reason:why,...(action==='suspend'?{reasonCode}:{}),...(caseId?{caseId}:{})});
      const next=summary.account?.status??(action==='suspend'?'suspended':'active');
      setKnown(next);setOpen(false);setOutcome(action==='suspend'?revocationText(summary.revocation):null);onChanged?.(next);
    }catch(e:any){setError(e.message);}finally{setBusy(false);}
  };
  return <div className="dq-account">
    {showStatus&&current==='suspended'&&<span className="lc-chip crit">Account suspended</span>}
    <button className={`dq-pill ${action==='suspend'?'danger':'plain'}`} disabled={busy}
      onClick={()=>{setOpen(v=>!v);setReason(action==='suspend'?defaultReason:'');setReasonCode('');setError('');}}>
      {action==='suspend'?'Suspend account':'Restore account'}</button>
    {outcome&&<span className={`dq-account-outcome${outcome.warn?' warn':''}`} role="status">{outcome.text}</span>}
    {open&&<div className="dq-ask dq-account-ask">
      <strong>{action==='suspend'?`Suspend ${name}'s account?`:`Restore ${name}'s account?`}</strong>
      <span className="lc-meta">{accountNote[action]}</span>
      {action==='suspend'&&devicesLine(counts)&&<span className="lc-meta dq-devices-line">{devicesLine(counts)}</span>}
      {action==='suspend'&&<>
        <label className="lc-meta" htmlFor="dq-reason-code">Reason shown to the user</label>
        <select id="dq-reason-code" aria-label="Reason shown to the user" value={reasonCode} disabled={busy}
          onChange={e=>setReasonCode(e.target.value as AccountReasonCode|'')}>
          <option value="">Choose a reason…</option>
          {ACCOUNT_REASONS.map(r=><option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
        <span className="lc-meta">Shown to the person in the app (newest versions). Pick the closest one.</span>
        <label className="lc-meta" htmlFor="dq-internal-note">Internal note, never shown to the user</label>
      </>}
      <textarea id="dq-internal-note" ref={box} aria-label="Reason for the account action" maxLength={2000} value={reason}
        placeholder={action==='suspend'?'Internal note. Why? It is kept with the account.':'Why? It is kept with the account.'} onChange={e=>setReason(e.target.value)}
        onKeyDown={e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey))void apply();if(e.key==='Escape')setOpen(false);}}/>
      {error&&<span className="lr-error" role="alert">{error}</span>}
      <div className="dq-acts">
        <button className={`dq-pill ${action==='suspend'?'stop':'good'}`} disabled={busy} onClick={()=>void apply()}>
          {busy?'Applying…':action==='suspend'?'Suspend account':'Restore account'}</button>
        <button className="dq-pill plain" disabled={busy} onClick={()=>setOpen(false)}>Cancel</button>
      </div>
    </div>}
  </div>;
}
